import { Button, Flex, Space, Tag, Typography } from 'antd';
import { EnvironmentOutlined, EyeOutlined } from '@ant-design/icons';
import type { HoveredLocation } from '~/app/dashboard/components/SupervisorOperationsMapCard.types';
import {
  formatSiteLocation,
  getOperationalTagColor,
} from '~/app/dashboard/components/supervisorOperationsMapCard.utils';

const { Text, Title } = Typography;

type SupervisorOperationsMapDetailsPanelProps = {
  activeHoveredLocation: HoveredLocation;
  onOpenLocationAction: (location: HoveredLocation) => void;
  onOpenParentSiteAction: (siteId: string) => void;
};

export function SupervisorOperationsMapDetailsPanel({
  activeHoveredLocation,
  onOpenLocationAction,
  onOpenParentSiteAction,
}: SupervisorOperationsMapDetailsPanelProps) {
  const hoveredSite =
    activeHoveredLocation.kind === 'site' ? activeHoveredLocation.data : null;
  const hoveredZone =
    activeHoveredLocation.kind === 'zone' ? activeHoveredLocation.data : null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: 320,
        maxWidth: 'calc(100% - 32px)',
        borderRadius: 16,
        padding: 16,
        background: 'rgba(255,255,255,0.96)',
        border: '1px solid #d8e2ec',
        boxShadow: '0 16px 30px rgba(15, 23, 42, 0.12)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Flex vertical gap={12}>
        <Flex justify="space-between" align="flex-start" gap={12}>
          <div>
            <Space size={8} wrap>
              <Tag
                color={activeHoveredLocation.kind === 'site' ? 'blue' : 'green'}
                style={{ marginInlineEnd: 0, borderRadius: 999 }}
              >
                {activeHoveredLocation.kind === 'site' ? 'Site' : 'Zone'}
              </Tag>
              {hoveredZone?.isMain ? (
                <Tag
                  color="gold"
                  style={{ marginInlineEnd: 0, borderRadius: 999 }}
                >
                  Main zone
                </Tag>
              ) : null}
            </Space>
            <Title level={5} style={{ margin: '10px 0 0' }}>
              {hoveredSite?.name ?? hoveredZone?.name}
            </Title>
            <Text type="secondary">
              {hoveredSite
                ? formatSiteLocation(hoveredSite)
                : hoveredZone?.siteName}
            </Text>
          </div>
          <Tag
            color={getOperationalTagColor(
              hoveredSite ??
                hoveredZone ?? { criticalAssets: 0, warningAssets: 0 },
            )}
            style={{ marginInlineEnd: 0, borderRadius: 999 }}
          >
            {hoveredSite?.totalOpenIncidents ??
              hoveredZone?.totalOpenIncidents ??
              0}{' '}
            open
          </Tag>
        </Flex>

        <Flex gap={12} wrap="wrap">
          <div
            style={{
              minWidth: 88,
              padding: '10px 12px',
              borderRadius: 12,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Assets
            </Text>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>
              {hoveredSite?.totalAssets ?? hoveredZone?.totalAssets ?? 0}
            </div>
          </div>
          <div
            style={{
              minWidth: 88,
              padding: '10px 12px',
              borderRadius: 12,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Healthy
            </Text>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>
              {hoveredSite?.healthyAssets ?? hoveredZone?.healthyAssets ?? 0}
            </div>
          </div>
          <div
            style={{
              minWidth: 88,
              padding: '10px 12px',
              borderRadius: 12,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Half affected
            </Text>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#d97706' }}>
              {hoveredSite?.warningAssets ?? hoveredZone?.warningAssets ?? 0}
            </div>
          </div>
          <div
            style={{
              minWidth: 88,
              padding: '10px 12px',
              borderRadius: 12,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Critical
            </Text>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>
              {hoveredSite?.criticalAssets ?? hoveredZone?.criticalAssets ?? 0}
            </div>
          </div>
        </Flex>

        <Space wrap>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => onOpenLocationAction(activeHoveredLocation)}
          >
            {hoveredSite ? 'Open site' : 'Open zone'}
          </Button>
          {hoveredZone ? (
            <Button
              icon={<EnvironmentOutlined />}
              onClick={() => onOpenParentSiteAction(hoveredZone.siteId)}
            >
              Open parent site
            </Button>
          ) : null}
        </Space>
      </Flex>
    </div>
  );
}
